import React, { useState } from 'react';
import { Mail, Copy, Check, CheckCircle2 } from 'lucide-react';
import { useTheme } from '../../lib/theme';
import { useAuth } from '../../lib/auth';
import { submitSupportTicket } from '../../lib/api';
import { SectionShell, SettingsCard, textInput, primaryBtn } from './_shared';

const SUPPORT_EMAIL = 'support@clasifi.ca';

export default function SupportSection({ onToast }) {
  const { colors } = useTheme();
  const { user } = useAuth();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setBusy(true);
    try {
      await submitSupportTicket({
        subject: subject.trim(),
        message: message.trim(),
        email: user?.email,
      });
      setSent(true);
      setSubject('');
      setMessage('');
      onToast?.('Support ticket submitted');
    } catch (ex) {
      onToast?.(ex?.detail || 'Could not submit ticket');
    } finally {
      setBusy(false);
    }
  };

  const copyEmail = async () => {
    try { await navigator.clipboard.writeText(SUPPORT_EMAIL); setCopied(true); setTimeout(() => setCopied(false), 1600); }
    catch { /* ignore */ }
  };

  return (
    <SectionShell eyebrow="Support" title="Support" description="We'll get back to you within 1 business day." testid="section-support">
      <SettingsCard testid="support-email-card">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: colors.accentSoft, color: colors.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Mail size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: colors.text, fontSize: 14 }}>Prefer email?</div>
            <div style={{ color: colors.textMuted, fontSize: 13 }}>Reach us directly at <strong style={{ color: colors.text }}>{SUPPORT_EMAIL}</strong></div>
          </div>
          <button type="button" onClick={copyEmail} data-testid="support-copy-email" style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 8, background: 'transparent', color: colors.text, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {copied ? <><Check size={14} color={colors.accent} /> Copied</> : <><Copy size={14} /> Copy</>}
          </button>
        </div>
      </SettingsCard>

      <SettingsCard title="Open a ticket" description={`We'll reply to ${user?.email}.`} testid="support-form-card">
        {sent ? (
          <div data-testid="support-sent" style={{ padding: 16, borderRadius: 10, background: colors.accentSoft, display: 'flex', gap: 10, alignItems: 'center' }}>
            <CheckCircle2 size={18} color={colors.accent} />
            <span style={{ color: colors.text, fontSize: 14 }}>Thanks — your ticket is in. We'll reply within 1 business day.</span>
            <button type="button" onClick={() => setSent(false)} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: colors.accent, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Open another</button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ color: colors.text, fontSize: 13, fontWeight: 600 }}>
              Subject
              <input
                type="text" required maxLength={120}
                value={subject} onChange={(e) => setSubject(e.target.value)}
                data-testid="support-subject"
                placeholder="A quick summary"
                style={{ ...textInput(colors), marginTop: 6 }}
              />
            </label>
            <label style={{ color: colors.text, fontSize: 13, fontWeight: 600 }}>
              Message
              <textarea
                required maxLength={4000} rows={5}
                value={message} onChange={(e) => setMessage(e.target.value)}
                data-testid="support-message"
                placeholder="Tell us what's going on, screenshots or steps help us help you faster."
                style={{ ...textInput(colors), marginTop: 6, resize: 'vertical', minHeight: 120 }}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" disabled={busy} data-testid="support-submit" style={primaryBtn(colors, busy)}>
                {busy ? 'Sending…' : 'Submit ticket'}
              </button>
            </div>
          </form>
        )}
      </SettingsCard>
    </SectionShell>
  );
}
